import React, { FC, useMemo, useRef } from "react";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Textarea } from "@shared/ui/textarea";
import { DivisionSearchSelect, LabouringSearchSelect } from "@src/components/business-data-entry";
import { BusinessForm, type BusinessFormRenderProps, type FormHandle } from "@src/components/business-form";
import { FormPanel } from "@shared/ui/form-panel";
import notification from "@app/services/notification";
import { wsInplantsApi } from "../../../../../../api/wsInplants";
import { reportError } from "@app/services/error-reporter";
import { arrayIsEmpty } from "@src/helpers/array/arrayIsEmpty";
import useCurrentUserStore from "@src/store/CurrentUserStore";
import type { WsInplantFilter } from "../../../../../../api/wsInplants";
import {
    wsInplantInsertFormConfig,
    wsInplantInsertToApi,
    type WsInplantInsertFormValues,
} from "./wsInplantInsert.form-config";

/** Пропсы {@link WsInplantInsertForm}. */
export interface WsInplantInsertFormProps {
    /** Текущий применённый фильтр списка — для дефолтов отправителя/получателя. */
    initialFilter?: WsInplantFilter | null;
    /** Колбэк после успешной валидации формы (обычно `createMutation.mutateAsync`). */
    onSubmit: (values: WsInplantInsertFormValues) => void | Promise<void>;
}

/**
 * Форма создания ВЗН УП (расход).
 */
export const WsInplantInsertForm: FC<WsInplantInsertFormProps> = ({ initialFilter = null, onSubmit }) => {
    const formApiRef = useRef<FormHandle<WsInplantInsertFormValues> | null>(null);
    const formRenderRef = useRef<BusinessFormRenderProps<WsInplantInsertFormValues> | null>(null);
    const labouring = useCurrentUserStore((state) => state.labouring);

    const initialValues = useMemo(() => {
        const defaults = wsInplantInsertFormConfig.defaults();
        if (initialFilter != null) {
            if (!arrayIsEmpty(initialFilter.SenderCodes)) {
                defaults.senderCode = initialFilter.SenderCodes[0];
            }
            if (!arrayIsEmpty(initialFilter.ReceiverCodes)) {
                defaults.receiverCode = initialFilter.ReceiverCodes[0];
            }
        }
        if (labouring != null) {
            defaults.leaveLabouringCode = labouring.Code;
        }
        return defaults;
    }, [initialFilter, labouring]);

    const handleGenInplantNum = async (): Promise<void> => {
        const values = formRenderRef.current?.getValues(); // TODO: formApiRef.current?.getValues() или хз, ну типа тут кринж сейчас с formRenderRef
        if (values == null) return;
        const payload = wsInplantInsertToApi(values);
        const result = await wsInplantsApi.defineNumber({ data: payload });
        if (!arrayIsEmpty(result.errors)) {
            const errorBody = result.errors.map((error) => `- ${error}`).join("\n");
            notification.warning(`Не удалось сгенерировать номер ВЗН:\n${errorBody}`);
            return;
        }
        formRenderRef.current?.setValue("num", result.num, { shouldValidate: true });
    };

    return (
        <FormPanel
            formApiRef={formApiRef}
            formProps={{
                formConfig: wsInplantInsertFormConfig,
                initialValues,
                onSubmit,
            }}
        >
            {(form) => {
                formRenderRef.current = form;
                return (
                    <>
                        <BusinessForm.Field name="num">
                            <Input
                                {...form.field("num").getPropsWithLabel()}
                                endDecorator={
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        type="button"
                                        aria-label="Сгенерировать номер"
                                        onClick={() => {
                                            handleGenInplantNum().catch((error) =>
                                                reportError(error, { from: "WsInplantInsertForm.genNum" }),
                                            );
                                        }}
                                    >
                                        ↻
                                    </Button>
                                }
                            />
                        </BusinessForm.Field>
                        <BusinessForm.Field name="senderCode">
                            <DivisionSearchSelect
                                {...form.field("senderCode").getSearchSelectProps((division) => division?.Code ?? null)}
                                flt={{ AvailableFor: "Create" }}
                            />
                        </BusinessForm.Field>
                        <BusinessForm.Field name="receiverCode">
                            <DivisionSearchSelect
                                {...form.field("receiverCode").getSearchSelectProps((division) => division?.Code ?? null)}
                            />
                        </BusinessForm.Field>
                        <BusinessForm.Field name="leaveLabouringCode">
                            <LabouringSearchSelect
                                {...form.field("leaveLabouringCode").getSearchSelectProps((lab) => lab?.Code ?? null)}
                                fullscreenTitle="Выберите МОЛ, который выдал ВЗН"
                            />
                        </BusinessForm.Field>
                        <BusinessForm.Field name="leaveMoveDate">
                            <Input.Date {...form.field("leaveMoveDate").getDateInputProps()} />
                        </BusinessForm.Field>
                        <BusinessForm.Field name="arrivalLabouringCode">
                            <LabouringSearchSelect
                                {...form.field("arrivalLabouringCode").getSearchSelectProps((lab) => lab?.Code ?? null)}
                                fullscreenTitle="Выберите МОЛ, который принял ВЗН"
                            />
                        </BusinessForm.Field>
                        <BusinessForm.Field name="arrivalMoveDate">
                            <Input.Date {...form.field("arrivalMoveDate").getDateInputProps()} />
                        </BusinessForm.Field>
                        <BusinessForm.Field name="notice">
                            <Textarea {...form.field("notice").getPropsWithLabel()} rows={3} />
                        </BusinessForm.Field>
                    </>
                );
            }}
        </FormPanel>
    );
};
